import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import { IAdsPost, IClientConfigs } from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { INotificationsService } from '../NotificationsService';
import { IPlayerService } from '../PlayerService';
import { IAdsService } from './adsServiceTypes';

interface TransformedAdItem {
	id: string;
	author: string;
	time: number;
	text: string;
	isOwner: boolean;
	phoneNumber: string;
	callsAvailable: boolean;
	status: 'pending' | 'published';
}

export class AdsService implements IAdsService {
	private readonly feedAds = new Subject<TransformedAdItem[]>([]);
	private readonly myAds = new Subject<TransformedAdItem[]>([]);
	private readonly adsSettings = new Subject<
		IClientConfigs['phone']['ads'] | null
	>(null);

	constructor(
		private readonly gameConfig: GameConfigService,
		private readonly backend: BackendService,
		private readonly notifications: INotificationsService,
		private readonly player: IPlayerService,
	) {
		this.gameConfig.getConfigs().subscribe((configs) => {
			if (!configs?.phone) {
				return;
			}
			this.adsSettings.next(configs.phone.ads ?? null);
		});

		this.backend.addEventHandler('ads:onAdd', (ad: IAdsPost) => {
			this.handleNewAd(ad);
		});

		this.backend.addEventHandler('ads:onRemove', (ad: IAdsPost) => {
			this.handleRemoveAd(ad);
		});

		this.player.waitForCharacterSelect().then(() => {
			this.performInitialFetch().catch((err) =>
				this.notifications.showException(err),
			);
		});
	}

	private async performInitialFetch(): Promise<void> {
		const feed = await this.fetchAllPublishedAds();
		const queued = await this.fetchQueuedAds();
		this.updateAdsLists(feed, queued);
	}

	@notifyOnError
	private async fetchAllPublishedAds(): Promise<IAdsPost[]> {
		const feed = await this.backend.invoke<IAdsPost[]>('ads:getFeed');
		return feed ?? [];
	}

	@notifyOnError
	private async fetchQueuedAds(): Promise<IAdsPost[]> {
		const queued = await this.backend.invoke<IAdsPost[]>('ads:getQueued');
		return queued ?? [];
	}

	private handleNewAd(ad: IAdsPost): void {
		const currentCharacterId = this.player.getCharacterId().getValue();
		if (!currentCharacterId) return;

		const isOwner = ad.characterId === currentCharacterId;

		if (isOwner) {
			if (ad.publishedAt) {
				this.moveFromQueuedToPublished(ad);
			} else {
				this.addToQueued(ad);
			}
		} else {
			this.addToFeed(ad);
		}
	}

	private handleRemoveAd(ad: IAdsPost): void {
		const currentCharacterId = this.player.getCharacterId().getValue();
		if (!currentCharacterId) return;

		const isOwner = ad.characterId === currentCharacterId;

		if (isOwner) {
			this.removeFromMyAds(ad.id!);
		} else {
			this.removeFromFeed(ad.id!);
		}
	}

	private moveFromQueuedToPublished(ad: IAdsPost): void {
		const currentMyAds = this.myAds.getValue();
		const filtered = currentMyAds.filter((a) => a.id !== ad.id);
		const transformed = this.transformAd(ad, true);
		this.myAds.next([transformed, ...filtered]);
	}

	private addToQueued(ad: IAdsPost): void {
		const currentMyAds = this.myAds.getValue();
		const transformed = this.transformAd(ad, true);
		this.myAds.next([transformed, ...currentMyAds]);
	}

	private addToFeed(ad: IAdsPost): void {
		const currentFeed = this.feedAds.getValue();
		const transformed = this.transformAd(ad, false);
		this.feedAds.next([transformed, ...currentFeed]);
	}

	private removeFromMyAds(id: string): void {
		const currentMyAds = this.myAds.getValue();
		const filtered = currentMyAds.filter((a) => a.id !== id);
		this.myAds.next(filtered);
	}

	private removeFromFeed(id: string): void {
		const currentFeed = this.feedAds.getValue();
		const filtered = currentFeed.filter((a) => a.id !== id);
		this.feedAds.next(filtered);
	}

	private updateAdsLists(feed: IAdsPost[], queued: IAdsPost[]): void {
		const currentCharacterId = this.player.getCharacterId().getValue();
		if (!currentCharacterId) return;

		const notOwned: TransformedAdItem[] = [];
		const ownedPublished: TransformedAdItem[] = [];
		const ownedQueued: TransformedAdItem[] = [];

		feed.forEach((ad) => {
			if (ad.characterId === currentCharacterId) {
				ownedPublished.push(this.transformAd(ad, true));
			} else {
				notOwned.push(this.transformAd(ad, false));
			}
		});

		queued.forEach((ad) => {
			ownedQueued.push(this.transformAd(ad, true));
		});

		notOwned.sort((a, b) => b.time - a.time);
		ownedPublished.sort((a, b) => b.time - a.time);
		ownedQueued.sort((a, b) => b.time - a.time);

		this.feedAds.next(notOwned);
		this.myAds.next([...ownedPublished, ...ownedQueued]);
	}

	private transformAd(ad: IAdsPost, isMyAd: boolean): TransformedAdItem {
		return {
			id: ad.id!,
			author: ad.name,
			time: ad.publishedAt || ad.createdAt,
			text: ad.text,
			isOwner: isMyAd,
			phoneNumber: ad.phoneNumber || '',
			callsAvailable: !!ad.phoneNumber,
			status: ad.publishedAt ? 'published' : 'pending',
		};
	}

	public getFeedAds(): IObservable<TransformedAdItem[]> {
		return this.feedAds;
	}

	public getMyAds(): IObservable<TransformedAdItem[]> {
		return this.myAds;
	}

	public getAdsSettings(): IObservable<IClientConfigs['phone']['ads'] | null> {
		return this.adsSettings;
	}

	@notifyOnError
	public async createAd(
		text: string,
		showPhone: boolean,
		urgent: boolean,
	): Promise<void> {
		await this.backend.invoke('ads:create', {
			text,
			showPhone,
			urgent,
		});
	}

	@notifyOnError
	public async deleteAd(id: string): Promise<void> {
		await this.backend.invoke('ads:delete', { id });
	}

	@notifyOnError
	public async renewAd(id: string): Promise<void> {
		await this.backend.invoke('ads:renew', { id });
	}
}
